import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import ExportExcel from "../../components/ExportExcel";
import { toast } from "react-toastify";
import { monthsList } from "../../utils";
import { capitalizeWords, formatDate } from "../../functions";
import * as XLSX from "xlsx";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDuesFeeDetails } from "../../redux/duesFeeSlice";

function DuesFee() {
  const dispatch = useDispatch();
  const {
    data: duesFeeDetails,
    loading,
    error,
  } = useSelector((state) => state.duesFee);

  useEffect(() => {
    dispatch(fetchDuesFeeDetails());
  }, [dispatch]);

  const dues = duesFeeDetails.filter(
    (student) => Number(student.monthlyFee || 0) > 0,
  );

  const monthList = [
    "All",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
    "january",
    "february",
    "march",
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedClass, setSelectedClass] = useState("All");
  const [selectedSection, setSelectedSection] = useState("All");
  const [selectedMonthForReport, setSelectedMonthForReport] = useState(null);

  // ✅ Fix 1: safe fallbacks for class/section lists
  const allClasses = Array.from(
    new Set(dues.map((s) => s.studentDetails?.currentClass).filter(Boolean)),
  ).sort();

  const allSections = Array.from(
    new Set(dues.map((s) => s.studentDetails?.currentSection).filter(Boolean)),
  ).sort();

  const monthScrollRef = useRef(null);
  const classScrollRef = useRef(null);
  const sectionScrollRef = useRef(null);

  const scrollLeft = (ref) => {
    if (ref.current) ref.current.scrollBy({ left: -150, behavior: "smooth" });
  };

  const scrollRight = (ref) => {
    if (ref.current) ref.current.scrollBy({ left: 150, behavior: "smooth" });
  };

  // ✅ Fix 2: safe toLowerCase with optional chaining + fallback
  const filteredRecords = dues.filter((student) => {
    const currentClass = student.studentDetails?.currentClass || "";
    const currentSection = student.studentDetails?.currentSection || "";
    const name = student.studentDetails?.name || "";
    const admission_no = student.studentDetails?.admission_no || "";

    const matchesMonth =
      selectedMonth === "All" ||
      (student.unpaidMonths || []).includes(selectedMonth);
    const matchesClass =
      selectedClass === "All" || currentClass === selectedClass;
    const matchesSection =
      selectedSection === "All" || currentSection === selectedSection;
    const matchesSearch =
      searchTerm === "" ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admission_no.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesMonth && matchesClass && matchesSection && matchesSearch;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const columnMapping = {
    "studentDetails.studentId": "Student ID",
    "studentDetails.admission_no": "Admission No",
    "studentDetails.name": "Name",
    "studentDetails.currentClass": "Class",
    "studentDetails.currentSection": "Section",
    "studentDetails.rollNo": "Roll No",
    "studentDetails.phoneNo": "Phone No",
    "studentDetails.gender": "Gender",
    "transportFacility.busStop": "Bus Stop",
    "transportFacility.vehicleNo": "Vehicle No",
    "transportFacility.busNumber": "Bus No",
    scholarshipType: "Scholarship Type",
    unpaidMonths: "Unpaid Months",
    monthlyFee: "Monthly Fee",
    transportFee: "Transport Fee",
  };

  const handleExportExcel = () => {
    ExportExcel(dues, "ScholarshipDetails", formattedData);
  };

  const unpaidMonthSet = new Set();
  dues.forEach((student) => {
    (student.unpaidMonths || []).forEach((month) => unpaidMonthSet.add(month));
  });
  const availableUnpaidMonths = Array.from(unpaidMonthSet).sort(
    (a, b) => monthsList.indexOf(a) - monthsList.indexOf(b),
  );

  const calculateTotals = () => {
    let monthlyTotal = 0;
    let transportTotal = 0;
    let siblingTotal = 0;

    filteredRecords.forEach((student) => {
      const unpaidMonths = student.unpaidMonths || [];
      const monthlyFee = student.monthlyFee || 0;
      const transportFee = student.transportFee || 0;

      const monthsToCount =
        selectedMonth === "All"
          ? unpaidMonths
          : unpaidMonths.includes(selectedMonth)
            ? [selectedMonth]
            : [];

      const transportableMonths = monthsToCount.filter((m) => m !== "June");

      const studentTotalDue =
        monthlyFee * monthsToCount.length +
        transportFee * transportableMonths.length;

      monthlyTotal += monthlyFee * monthsToCount.length;
      transportTotal += transportFee * transportableMonths.length;

      if (student.scholarshipType === "sibling") {
        siblingTotal += studentTotalDue;
      }
    });

    return {
      totalMonthlyDue: monthlyTotal,
      totalTransportDue: transportTotal,
      totalDueAllStudents: monthlyTotal + transportTotal,
      totalDueOfSiblingStudents: siblingTotal,
    };
  };

  const {
    totalMonthlyDue,
    totalTransportDue,
    totalDueAllStudents,
    totalDueOfSiblingStudents,
  } = calculateTotals();

  const formattedData = dues.map((item) => {
    const unpaidMonths = Array.isArray(item.unpaidMonths)
      ? item.unpaidMonths
      : [];
    const monthlyFee = item.monthlyFee || 0;
    const transportFee = item.transportFee || 0;

    const monthsForTransport = unpaidMonths.filter((month) => month !== "June");

    const totalMonthlyFee = monthlyFee * unpaidMonths.length;
    const totalTransportFee = transportFee * monthsForTransport.length;
    const totalDues = totalMonthlyFee + totalTransportFee;

    return {
      "Admission No": item.studentDetails?.admission_no || "",
      Name: item.studentDetails?.name || "",
      Class: item.studentDetails?.currentClass || "",
      Section: item.studentDetails?.currentSection || "",
      "Roll No": item.studentDetails?.rollNo || "",
      "Phone No": item.studentDetails?.phoneNo || "",
      Gender: item.studentDetails?.gender || "",
      "Scholarship Type": item.scholarshipType || "",
      "Unpaid Months": unpaidMonths.join(", "),
      "Monthly Fee": monthlyFee,
      "Transport Fee": transportFee,
      "Total Monthly Fee": totalMonthlyFee,
      "Total Transport Fee": totalTransportFee,
      "Total Dues": totalDues,
    };
  });

  const getValueByPath = (obj, path) => {
    return path.split(".").reduce((acc, key) => {
      if (acc === undefined || acc === null) return "";
      const match = key.match(/(.*?)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const index = parseInt(match[2], 10);
        return acc[arrayKey] && acc[arrayKey][index] !== undefined
          ? acc[arrayKey][index]
          : "";
      }
      return acc[key] !== undefined ? acc[key] : "";
    }, obj);
  };

  const handleExportClassWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const classGroups = {};

    dues.forEach((student) => {
      const cls = student.studentDetails?.currentClass || "Unknown";
      const sec = student.studentDetails?.currentSection || "Unknown";

      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);
        if (Array.isArray(value)) value = value.join(", ");
        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }
        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }
        formattedItem[header] = value;
      });

      const unpaidMonths = Array.isArray(student.unpaidMonths)
        ? student.unpaidMonths
        : [];
      const monthlyFee = student.monthlyFee || 0;
      const transportFee = student.transportFee || 0;

      const monthsToCount =
        selectedMonth === "All"
          ? unpaidMonths
          : unpaidMonths.includes(selectedMonth)
            ? [selectedMonth]
            : [];

      const transportableMonths = monthsToCount.filter((m) => m !== "June");

      formattedItem["Total Monthly Fee"] = monthlyFee * monthsToCount.length;
      formattedItem["Total Transport Fee"] =
        transportFee * transportableMonths.length;
      formattedItem["Total Dues"] =
        formattedItem["Total Monthly Fee"] +
        formattedItem["Total Transport Fee"];

      classGroups[cls][sec].push(formattedItem);
    });

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];
      const sortedSections = Object.keys(sections).sort();

      sortedSections.forEach((sec) => {
        const students = sections[sec];
        students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
        combinedData.push({ "Student ID": `Section ${sec}` });
        combinedData.push(...students);
        combinedData.push({});
      });

      const worksheet = XLSX.utils.json_to_sheet(combinedData, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, className.slice(0, 31));
    });

    XLSX.writeFile(workbook, `DuesFee_ClassWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportGenderWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const genderGroups = {};

    dues.forEach((student) => {
      const gender = student.studentDetails?.gender || "Unknown";
      if (!genderGroups[gender]) genderGroups[gender] = [];

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);
        if (Array.isArray(value)) value = value.join(", ");
        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }
        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }
        formattedItem[header] = value;
      });

      genderGroups[gender].push(formattedItem);
    });

    Object.entries(genderGroups).forEach(([gender, students]) => {
      students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
      const worksheet = XLSX.utils.json_to_sheet(students, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, gender.slice(0, 31));
    });

    XLSX.writeFile(workbook, `DuesFee_GenderWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAvailingTransportWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const availingTransportList = [];

    dues.forEach((student) => {
      const busStop = student.transportFacility?.busStop;
      if (!busStop) return;

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);
        if (Array.isArray(value)) value = value.join(", ");
        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }
        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }
        formattedItem[header] = value;
      });

      availingTransportList.push(formattedItem);
    });

    if (availingTransportList.length === 0) {
      alert("No students found availing transport.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(availingTransportList, {
      skipHeader: false,
    });
    XLSX.utils.book_append_sheet(workbook, worksheet, "Availing Transport");
    XLSX.writeFile(workbook, `DuesFee_AvailingTransport_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportClassMonthWiseExcel = (month) => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    if (!month || month === "All") {
      handleExportClassWiseExcel();
      return;
    }

    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const classGroups = {};

    const monthIndex = monthList.findIndex(
      (m) => m.toLowerCase() === month.toLowerCase(),
    );

    dues.forEach((student) => {
      const unpaidMonths = (student.unpaidMonths || []).map((m) =>
        m.toLowerCase(),
      );

      const hasUnpaidUptoSelected = unpaidMonths.some((m) => {
        const idx = monthList.indexOf(m);
        return idx > 0 && idx <= monthIndex;
      });

      if (!hasUnpaidUptoSelected) return;

      const cls = student.studentDetails?.currentClass || "Unknown";
      const sec = student.studentDetails?.currentSection || "Unknown";

      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);
        if (Array.isArray(value)) value = value.join(", ");
        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }
        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }
        formattedItem[header] = value;
      });

      const monthlyFee = student.monthlyFee || 0;
      const transportFee = student.transportFee || 0;

      const monthsToCount = unpaidMonths.filter(
        (m) => monthList.indexOf(m) > 0 && monthList.indexOf(m) <= monthIndex,
      );

      const transportableMonths = monthsToCount.filter((m) => m !== "june");

      formattedItem["Unpaid Months"] = monthsToCount
        .slice()
        .sort((a, b) => monthList.indexOf(a) - monthList.indexOf(b))
        .join(", ");

      formattedItem["Dues Upto Month"] = month;
      formattedItem["Total Monthly Fee"] = monthlyFee * monthsToCount.length;
      formattedItem["Total Transport Fee"] =
        transportFee * transportableMonths.length;
      formattedItem["Total Dues"] =
        formattedItem["Total Monthly Fee"] +
        formattedItem["Total Transport Fee"];

      classGroups[cls][sec].push(formattedItem);
    });

    if (Object.keys(classGroups).length === 0) {
      toast.error("No students found for this month");
      return;
    }

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];
      const sortedSections = Object.keys(sections).sort();

      sortedSections.forEach((sec) => {
        const students = sections[sec];
        students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
        combinedData.push({ "Student ID": `Section ${sec}` });
        combinedData.push(...students);
        combinedData.push({});
      });

      const worksheet = XLSX.utils.json_to_sheet(combinedData, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        `${className}`.slice(0, 31),
      );
    });

    const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
    if (closeButton) closeButton.click();

    XLSX.writeFile(workbook, `DuesFee_ClassMonthWise_${month}_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((student, index) => {
                          const unpaidMonths = student.unpaidMonths || [];
                          const monthlyFee = student.monthlyFee || 0;
                          const transportFee = student.transportFee || 0;

                          const monthsToCount =
                            selectedMonth === "All"
                              ? unpaidMonths
                              : unpaidMonths.includes(selectedMonth)
                                ? [selectedMonth]
                                : [];

                          const transportableMonths = monthsToCount.filter(
                            (m) => m !== "June",
                          );

                          const totalDue =
                            monthlyFee * monthsToCount.length +
                            transportFee * transportableMonths.length;

                          return (
                            <tr key={index}>
                              <td>{indexOfFirstRecord + index + 1}.</td>
                              <td className="lh-sm py-4 text-wrap">
                                <span className="text-dark">Student ID</span>{" "}
                                &mdash; {student.studentDetails.studentId}
                                <br />
                                <span className="text-dark">
                                  Admission No
                                </span>{" "}
                                &mdash; {student.studentDetails.admission_no}
                                <br />
                                <span className="text-dark">
                                  Student Name
                                </span>{" "}
                                &mdash; {student.studentDetails.name}
                                <br />
                                <span className="text-dark">
                                  Class
                                </span> &mdash;{" "}
                                {student.studentDetails.currentClass}
                                <br />
                                <span className="text-dark">Section</span>{" "}
                                &mdash; {student.studentDetails.currentSection}
                                <br />
                                <span className="text-dark">Roll No</span>{" "}
                                &mdash; {student.studentDetails.rollNo}
                                <br />
                                <span className="text-dark">Gender</span>{" "}
                                &mdash; {student.studentDetails.gender}
                                <br />
                                <span className="text-dark">Phone No</span>{" "}
                                &mdash; {student.studentDetails.phoneNo}
                                <br />
                                <span className="text-dark">
                                  Scholarship Type
                                </span>{" "}
                                &mdash;{" "}
                                {capitalizeWords(student.scholarshipType)}
                                <hr className="w-25" />
                                <span class="text-dark">
                                  Stoppage
                                </span> &mdash;{" "}
                                <span className="text-capitalize">
                                  {student.transportFacility.busStop || "NONE"}
                                </span>
                                <br />
                                <span class="text-dark">Vehicle No</span>{" "}
                                &mdash;{" "}
                                {student.transportFacility.vehicleNo || "N/A"}
                                <br />
                                <span class="text-dark">
                                  Bus No
                                </span> &mdash;{" "}
                                {student.transportFacility.busNumber || "N/A"}
                                <hr className="w-25" />
                                <span className="text-dark">
                                  Unpaid Months
                                </span>{" "}
                                &mdash;{" "}
                                {unpaidMonths
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      monthsList.indexOf(a) -
                                      monthsList.indexOf(b),
                                  )
                                  .map((month, index) => (
                                    <span key={index}>
                                      {month}
                                      {index < unpaidMonths.length - 1 && ", "}
                                      {(index + 1) % 4 === 0 && <br />}
                                    </span>
                                  ))}
                                <br />
                                <br />
                                <span className="text-dark">
                                  Monthly Fee
                                </span>{" "}
                                &mdash; {monthlyFee}
                                {transportFee !== 0 && (
                                  <>
                                    <br />
                                    <span className="text-dark">
                                      Transport Fee
                                    </span>{" "}
                                    &mdash; {transportFee}
                                  </>
                                )}
                                <br />
                                <span className="text-dark">
                                  Total Dues
                                </span>{" "}
                                &mdash; {totalDue}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1 width-fit-content">
                                  <Link
                                    to="/view-student"
                                    state={{
                                      admissionNo:
                                        student.studentDetails.admission_no,
                                    }}
                                    className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    View Details
                                  </Link>
                                  <Link
                                    to="/collect-fees"
                                    state={{
                                      admissionNo:
                                        student.studentDetails.admission_no,
                                    }}
                                    className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    Fee Ledger
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Fees Collection</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Dues Fee
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {filteredRecords.length.toLocaleString("en-IN")}
                        </h3>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Dues Fee
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.4 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  data-bs-toggle="modal"
                                  data-bs-target="#classSectionMonthWiseModal"
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class/Section Month Wise
                                </span>
                                <span
                                  onClick={handleExportClassWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class/Section Wise
                                </span>
                                <span
                                  onClick={handleExportGenderWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Gender Wise
                                </span>
                                <span
                                  onClick={
                                    handleExportAvailingTransportWiseExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Transport Availing
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>

                      {dues.length > 0 && (
                        <>
                          <div className="position-relative mt-3 mb-2">
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={() => scrollLeft(monthScrollRef)}
                            >
                              <i className="bi bi-chevron-left text-white"></i>
                            </span>

                            <div
                              className="gap-1 d-flex overflow-x-auto px-6"
                              ref={monthScrollRef}
                            >
                              <span
                                className={`btn btn-tag btn-sm ${
                                  selectedMonth === "All"
                                    ? "text-success-emphasis bg-success-subtle border border-success"
                                    : ""
                                }`}
                                onClick={() => setSelectedMonth("All")}
                              >
                                All
                              </span>
                              {availableUnpaidMonths.map((month) => (
                                <span
                                  key={month}
                                  className={`btn btn-tag btn-sm ${
                                    selectedMonth === month
                                      ? "text-success-emphasis bg-success-subtle border border-success"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedMonth(month)}
                                >
                                  {month}
                                </span>
                              ))}
                            </div>

                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={() => scrollRight(monthScrollRef)}
                            >
                              <i className="bi bi-chevron-right text-white"></i>
                            </span>
                          </div>

                          <div className="position-relative mt-3 mb-2">
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={() => scrollLeft(classScrollRef)}
                            >
                              <i className="bi bi-chevron-left text-white"></i>
                            </span>

                            <div
                              className="gap-1 d-flex overflow-x-auto px-6"
                              ref={classScrollRef}
                            >
                              <span
                                className={`btn btn-tag btn-sm ${
                                  selectedClass === "All"
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setSelectedClass("All")}
                              >
                                All
                              </span>
                              {allClasses.map((cls) => (
                                <span
                                  key={cls}
                                  className={`btn btn-tag btn-sm ${
                                    selectedClass === cls
                                      ? "bg-success-subtle border border-success text-success-emphasis"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedClass(cls)}
                                >
                                  {cls}
                                </span>
                              ))}
                            </div>

                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={() => scrollRight(classScrollRef)}
                            >
                              <i className="bi bi-chevron-right text-white"></i>
                            </span>
                          </div>

                          <div className="position-relative mb-3">
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={() => scrollLeft(sectionScrollRef)}
                            >
                              <i className="bi bi-chevron-left text-white"></i>
                            </span>

                            <div
                              className="gap-1 d-flex overflow-x-auto px-6"
                              ref={sectionScrollRef}
                            >
                              <span
                                className={`btn btn-tag btn-sm ${
                                  selectedSection === "All"
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setSelectedSection("All")}
                              >
                                All
                              </span>
                              {allSections.map((sec) => (
                                <span
                                  key={sec}
                                  className={`btn btn-tag btn-sm ${
                                    selectedSection === sec
                                      ? "bg-success-subtle border border-success text-success-emphasis"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedSection(sec)}
                                >
                                  {sec}
                                </span>
                              ))}
                            </div>

                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={() => scrollRight(sectionScrollRef)}
                            >
                              <i className="bi bi-chevron-right text-white"></i>
                            </span>
                          </div>

                          <div class="mt-1">
                            <div className="row g-1">
                              <div className="col-6">
                                <div className="p-3 bg-info-soft rounded-4">
                                  <h4 className="fw-bold m-0 text-center fs-4">
                                    {totalMonthlyDue.toLocaleString("en-IN")}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-sm">
                                    Total Monthly Fee
                                  </p>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="p-3 bg-info-soft rounded-4">
                                  <h4 className="fw-bold m-0 text-center fs-4">
                                    {totalTransportDue.toLocaleString("en-IN")}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-sm">
                                    Total Transport Fee
                                  </p>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="p-3 bg-success-soft rounded-4">
                                  <h4 className="fw-bold m-0 text-center fs-4">
                                    {totalDueAllStudents.toLocaleString(
                                      "en-IN",
                                    )}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-sm">
                                    Total Amount Due
                                  </p>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="p-3 bg-danger-soft rounded-4">
                                  <h4 className="fw-bold m-0 text-center fs-4">
                                    {totalDueOfSiblingStudents.toLocaleString(
                                      "en-IN",
                                    )}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-sm">
                                    From Sibling Students
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>

                  <div
                    class="modal m-0 fade"
                    id="classSectionMonthWiseModal"
                    tabindex="-1"
                    aria-labelledby="classSectionMonthWiseModalLabel"
                    aria-hidden="true"
                  >
                    <div class="modal-dialog modal-dialog-centered">
                      <div class="modal-content rounded-5 overflow-hidden">
                        <div class="p-4 bg-danger-soft d-flex justify-content-end">
                          <i
                            className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                          ></i>
                        </div>
                        <div class="pt-0 p-5">
                          <div className="d-flex justify-content-center mt-n8">
                            <img
                              src="/assets/images/report.jpg"
                              width="90"
                              className="object-fit-contain p-3 rounded-5 bg-white"
                            />
                          </div>

                          <div className="mt-2">
                            <h5 class="text-center lh-1 fw-medium">
                              Class / Section Month Wise Report
                              <br />
                              <span className="text-secondary">
                                Generate Report
                              </span>
                            </h5>
                          </div>

                          <div className="mt-4">
                            <div className="bg-light rounded-pill py-2 mb-3">
                              <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                                Select Month &nbsp;(Student's Fee Dues Upto)
                              </h5>
                            </div>

                            <div className="d-flex flex-wrap justify-content-center gap-1 px-1">
                              {monthList.map((month, index) => (
                                <div
                                  key={index}
                                  className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 text-capitalize ${
                                    selectedMonthForReport === month
                                      ? "bg-warning text-white border-warning"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    setSelectedMonthForReport(month)
                                  }
                                >
                                  {month}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
                            <div>
                              <button
                                onClick={() =>
                                  handleExportClassMonthWiseExcel(
                                    selectedMonthForReport,
                                  )
                                }
                                class="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
                              >
                                Download Report
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default DuesFee;
